import { Link, usePathname, type Href } from 'expo-router';
import {
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Container, Text } from '@/components/ui';
import { palette, useTheme } from '@/theme';

type NavItem = { key: string; href: Href };

const items: NavItem[] = [
  { key: 'home', href: '/' },
  { key: 'kurse', href: '/kurse' },
  { key: 'events', href: '/events' },
  { key: 'info', href: '/info' },
  { key: 'about', href: '/about' },
  { key: 'kontakt', href: '/kontakt' },
];

const MOBILE_BREAKPOINT = 760;

export function PrimaryNav() {
  const theme = useTheme();
  const { t } = useTranslation();
  const pathname = usePathname();
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' || pathname === '' : pathname?.startsWith(href);

  // Close menu on viewport widening
  useEffect(() => {
    if (!isMobile && menuOpen) setMenuOpen(false);
  }, [isMobile, menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <View
      style={[
        styles.outer,
        Platform.OS === 'web'
          ? ({
              position: 'sticky',
              backgroundColor: 'rgba(242,237,251,0.92)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            } as any)
          : { backgroundColor: theme.colors.bg },
      ]}
    >
      <Container>
        <View style={styles.row}>
          <Link href="/" asChild>
            <Pressable accessibilityRole="link" style={styles.brandPress}>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 30,
                  lineHeight: 30,
                  letterSpacing: -0.54,
                  color: theme.colors.fg,
                }}
              >
                Zouk
                <Text
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    color: theme.colors.accent,
                    fontSize: 30,
                  }}
                >
                  ess
                </Text>
                ence
              </Text>
            </Pressable>
          </Link>

          {!isMobile && (
            <View style={styles.links}>
              {items.map((it) => (
                <NavLink
                  key={it.key}
                  href={it.href}
                  label={t(`common.nav.${it.key}`)}
                  active={!!isActive(it.href as string)}
                />
              ))}
            </View>
          )}

          {isMobile ? (
            <HamburgerButton open={menuOpen} onPress={() => setMenuOpen((v) => !v)} />
          ) : (
            <Link href="/kontakt" asChild>
              <Pressable accessibilityRole="link" style={styles.ctaPress}>
                <CtaButton />
              </Pressable>
            </Link>
          )}
        </View>
      </Container>

      {isMobile && menuOpen ? (
        <MobileMenu isActive={isActive} onClose={() => setMenuOpen(false)} />
      ) : null}
    </View>
  );
}

function NavLink({ href, label, active }: { href: Href; label: string; active: boolean }) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const bg = active ? theme.colors.fg : hovered ? theme.colors.bgSoft : 'transparent';
  const color = active ? theme.colors.bg : hovered ? theme.colors.fg : theme.colors.fgSoft;
  const pressStyle: ViewStyle = StyleSheet.flatten([
    styles.linkPress,
    { backgroundColor: bg, borderRadius: 999 },
  ]) as ViewStyle;
  return (
    <Link href={href} asChild>
      <Pressable
        accessibilityRole="link"
        {...(Platform.OS === 'web'
          ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) }
          : {})}
        style={pressStyle}
      >
        <Text
          style={{
            color,
            fontFamily: theme.fontFamily.body as string,
            fontSize: 14,
          }}
        >
          {label}
        </Text>
      </Pressable>
    </Link>
  );
}

function CtaButton() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const bg = hovered ? theme.colors.accent : theme.colors.accentAlt;
  const hoverProps =
    Platform.OS === 'web'
      ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) }
      : {};

  return (
    <View
      {...(hoverProps as any)}
      style={[
        styles.cta,
        {
          backgroundColor: bg,
          borderRadius: 999,
          transform: hovered ? [{ translateY: -1 }] : undefined,
        },
      ]}
    >
      <Text style={{ color: palette.paper, fontSize: 14, fontWeight: '500', fontFamily: 'Geist_500Medium' }}>
        {t('common.cta.start')}
      </Text>
      <Text
        style={{
          color: palette.paper,
          fontSize: 14,
          marginLeft: 6,
          transform: [{ translateX: hovered ? 3 : 0 }],
        }}
      >
        →
      </Text>
    </View>
  );
}

function HamburgerButton({ open, onPress }: { open: boolean; onPress: () => void }) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={open ? t('common.nav.menuClose') : t('common.nav.menuOpen')}
      accessibilityState={{ expanded: open }}
      onPress={onPress}
      style={[
        styles.hamburger,
        {
          backgroundColor: theme.colors.fg,
          borderRadius: 999,
        },
      ]}
    >
      <View
        style={[
          styles.line,
          { backgroundColor: palette.paper },
          open
            ? ({
                transform: [{ translateY: 5 }, { rotate: '45deg' }],
              } as any)
            : null,
          Platform.OS === 'web'
            ? ({ transitionProperty: 'transform, opacity', transitionDuration: '180ms' } as any)
            : null,
        ]}
      />
      <View
        style={[
          styles.line,
          { backgroundColor: palette.paper, marginTop: 4 },
          open ? { opacity: 0 } : null,
          Platform.OS === 'web'
            ? ({ transitionProperty: 'opacity', transitionDuration: '180ms' } as any)
            : null,
        ]}
      />
      <View
        style={[
          styles.line,
          { backgroundColor: palette.paper, marginTop: 4 },
          open
            ? ({
                transform: [{ translateY: -5 }, { rotate: '-45deg' }],
              } as any)
            : null,
          Platform.OS === 'web'
            ? ({ transitionProperty: 'transform, opacity', transitionDuration: '180ms' } as any)
            : null,
        ]}
      />
    </Pressable>
  );
}

function MobileMenu({
  isActive,
  onClose,
}: {
  isActive: (href: string) => boolean | undefined;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <View
      style={[
        styles.menu,
        {
          backgroundColor: theme.colors.bg,
          borderTopColor: theme.colors.rule,
          borderBottomColor: theme.colors.rule,
        },
      ]}
    >
      <Container>
        <View style={styles.menuList}>
          {items.map((it) => (
            <MobileLink
              key={it.key}
              href={it.href}
              label={t(`common.nav.${it.key}`)}
              active={!!isActive(it.href as string)}
              onPress={onClose}
            />
          ))}
        </View>
        <View style={styles.menuFooter}>
          <Link href="/kontakt" asChild>
            <Pressable
              accessibilityRole="link"
              onPress={onClose}
              style={
                StyleSheet.flatten([
                  styles.cta,
                  { backgroundColor: theme.colors.accentAlt, borderRadius: 999 },
                ]) as ViewStyle
              }
            >
              <Text
                style={{
                  color: palette.paper,
                  fontSize: 15,
                  fontWeight: '500',
                  fontFamily: 'Geist_500Medium',
                }}
              >
                {t('common.cta.start')}
              </Text>
              <Text style={{ color: palette.paper, fontSize: 15, marginLeft: 6 }}>→</Text>
            </Pressable>
          </Link>
          <Text variant="meta" tone="fgMute" style={{ marginTop: 14 }}>
            {`${t('common.utility.email')} · ${t('common.utility.phone')}`}
          </Text>
        </View>
      </Container>
    </View>
  );
}

function MobileLink({
  href,
  label,
  active,
  onPress,
}: {
  href: Href;
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const pressStyle: ViewStyle = StyleSheet.flatten([
    styles.mobileItem,
    {
      backgroundColor: active ? theme.colors.fg : hovered ? theme.colors.bgSoft : 'transparent',
      borderRadius: 12,
    },
  ]) as ViewStyle;

  return (
    <Link href={href} asChild>
      <Pressable
        accessibilityRole="link"
        onPress={onPress}
        {...(Platform.OS === 'web'
          ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) }
          : {})}
        style={pressStyle}
      >
        <Text
          style={{
            fontFamily: theme.fontFamily.display,
            fontSize: 28,
            lineHeight: 32,
            color: active ? palette.paper : theme.colors.fg,
          }}
        >
          {label}
        </Text>
        <Text
          style={{
            color: active ? palette.emberSoft : theme.colors.accent,
            fontSize: 18,
          }}
        >
          →
        </Text>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  outer: {
    paddingVertical: 18,
    top: 0,
    zIndex: 50,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  },
  brandPress: {
    paddingVertical: 4,
  },
  links: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  linkPress: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  ctaPress: {
    alignSelf: 'flex-start',
  },
  cta: {
    paddingVertical: 12,
    paddingHorizontal: 22,
    flexDirection: 'row',
    alignItems: 'center',
  },
  hamburger: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  line: {
    width: 22,
    height: 2,
    borderRadius: 1,
  },
  menu: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    marginTop: 18,
    paddingVertical: 12,
  },
  menuList: {
    paddingVertical: 12,
    gap: 4,
  },
  mobileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  menuFooter: {
    paddingTop: 16,
    paddingBottom: 24,
    alignItems: 'flex-start',
  },
});
